/**
 * BookingConfirmedPage
 * 
 * Simple confirmation page shown to patients after they click
 * to confirm their appointment slot.
 * 
 * Handles:
 * - Success: Shows appointment details
 * - Already booked: Shows existing appointment message
 * - Error: Shows error message with contact info
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Calendar, Phone, Mail } from 'lucide-react';

function BookingConfirmedPage() {
  const [searchParams] = useSearchParams();
  
  const slot = searchParams.get('slot');
  const time = searchParams.get('time');
  const already = searchParams.get('already');
  const reason = searchParams.get('reason');

  // Error state
  if (reason) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 40,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: '#fee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <AlertCircle size={32} color="#d9534f" />
          </div>
          
          <h1 style={{ color: '#d9534f', marginBottom: 15 }}>
            Booking Issue
          </h1>
          
          <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
            {reason === 'taken' 
              ? "Sorry, this time slot has just been taken by another patient. Please select a different time or contact our reception team."
              : reason === 'expired'
              ? "This booking link has expired. Please contact our reception team to schedule your appointment."
              : "There was a problem confirming your appointment. Please contact our reception team."}
          </p>
          
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: 20,
            borderRadius: 8,
            marginTop: 20,
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Contact Reception:</p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              <Phone size={18} />
              <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
            </p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Mail size={18} />
              <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Already booked state
  if (already) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 40,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: '#e8f5e9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <CheckCircle size={32} color="#5cb85c" />
          </div>
          
          <h1 style={{ color: '#5cb85c', marginBottom: 15 }}>
            Already Confirmed
          </h1>
          
          <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
            You have already confirmed an appointment. Check your email for the confirmation details.
          </p>
          
          <p style={{ color: '#666', fontSize: 14 }}>
            Need to reschedule? Contact our reception team:
          </p>
          
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: 20,
            borderRadius: 8,
            marginTop: 15,
          }}>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
              <Phone size={18} />
              <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
            </p>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Mail size={18} />
              <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 40,
        maxWidth: 500,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}>
        <div style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#e8f5e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <CheckCircle size={48} color="#5cb85c" />
        </div>
        
        <h1 style={{ color: '#5cb85c', marginBottom: 15 }}>
          Appointment Confirmed!
        </h1>
        
        <p style={{ color: '#666', marginBottom: 25, fontSize: 16 }}>
          Thank you for confirming your telehealth appointment.
        </p>
        
        {/* Appointment Details */}
        <div style={{
          backgroundColor: '#e3f2fd',
          borderLeft: '4px solid #6b9bd1',
          padding: 20,
          borderRadius: '0 8px 8px 0',
          textAlign: 'left',
          marginBottom: 25,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Calendar size={20} color="#6b9bd1" />
            <span style={{ fontWeight: 'bold', fontSize: 18 }}>{slot || 'Your selected date'}</span>
          </div>
          {time && (
            <div style={{ marginLeft: 30, fontSize: 16, color: '#333' }}>
              at <strong>{time}</strong>
            </div>
          )}
        </div>
        
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          padding: 15,
          borderRadius: 8,
          marginBottom: 25,
          textAlign: 'left',
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#856404', marginBottom: 8 }}>
            📞 Phone Consultation
          </p>
          <p style={{ margin: 0, fontSize: 14, color: '#856404' }}>
            Dr Armstrong will call you on your mobile phone at the scheduled appointment time. Please ensure your phone is charged and you're in a quiet location with good reception.
          </p>
        </div>
        
        <h3 style={{ color: '#333', marginBottom: 10 }}>What's Next?</h3>
        <ul style={{ 
          textAlign: 'left', 
          color: '#666', 
          lineHeight: 1.8,
          paddingLeft: 20,
        }}>
          <li>Check your email for confirmation details</li>
          <li>Make sure your mobile phone is charged and has reception</li>
          <li>Find a quiet, private location for your consultation</li>
          <li>Have any relevant documents or imaging results handy</li>
          <li>Be available from 5 minutes before your appointment time</li>
        </ul>
        
        <div style={{
          backgroundColor: '#f0f7ff',
          border: '1px solid #6b9bd1',
          padding: 15,
          borderRadius: 8,
          marginBottom: 20,
          textAlign: 'left',
        }}>
          <p style={{ margin: 0, fontSize: 14, color: '#333' }}>
            <strong>Please note:</strong> This initial consultation is fully funded by ACC. If a follow-up appointment is required, there may be a patient surcharge. Your clinician will discuss this with you during your consultation.
          </p>
        </div>

        <div style={{
          backgroundColor: '#f5f5f5',
          padding: 20,
          borderRadius: 8,
          marginTop: 25,
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: 10 }}>Need to reschedule?</p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <Phone size={18} />
            <a href="tel:079576064" style={{ color: '#e8941c', fontWeight: 'bold' }}>07 957 6064</a>
          </p>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Mail size={18} />
            <a href="mailto:cambridge@angsm.co.nz" style={{ color: '#e8941c' }}>cambridge@angsm.co.nz</a>
          </p>
        </div>
        
        <p style={{ marginTop: 25, fontSize: 14, color: '#999' }}>
          Anglesea Sports Medicine | Cambridge, New Zealand
        </p>
      </div>
    </div>
  );
}

export default BookingConfirmedPage;
